import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft,
  RefreshCw,
  Plus,
  TrendingDown,
  DollarSign,
  Percent,
  Calculator,
  Loader2,
  AlertCircle,
  Archive,
} from 'lucide-react';
import { barrattAPI, type SpendLeakageReport } from '../lib/api';
import { format } from 'date-fns';
import { useFormatters } from '../contexts/PreferencesContext';
import { toast } from 'sonner';
import { statusColors, toNumber, formatPercentage, hasUploadSplit } from './spend-leakage/leakageFormat';
import { useLeakageItems } from './spend-leakage/useLeakageItems';
import LeakageItemsTable from './spend-leakage/LeakageItemsTable';
import LeakageUploadSplitTiles from './spend-leakage/LeakageUploadSplitTiles';
import {
  AddLeakageItemDialog,
  PublishReportDialog,
  ArchiveReportDialog,
  EMPTY_NEW_ITEM,
  type NewLeakageItem,
} from './spend-leakage/LeakageReportDialogs';

const SpendLeakageDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const reportId = id ? parseInt(id, 10) : null;
  const [report, setReport] = useState<SpendLeakageReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addItemDialogOpen, setAddItemDialogOpen] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [newItem, setNewItem] = useState<NewLeakageItem>(EMPTY_NEW_ITEM);

  // Items always come from the paginated endpoint — the report no longer embeds them.
  const leakageItems = useLeakageItems(report ? report.id : null);

  const { formatCurrency: contextFormatCurrency } = useFormatters();
  const formatCurrency = (amount: number | string) => contextFormatCurrency(toNumber(amount));

  const loadReport = useCallback(async () => {
    if (!reportId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await barrattAPI.getSpendLeakageReport(reportId);
      setReport(response.data);
    } catch (err) {
      console.error('Failed to load spend leakage report:', err);
      setError('Failed to load report. It may have been deleted or you may not have permission.');
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const refreshAll = () => {
    void loadReport();
    leakageItems.reload();
  };

  const handleAddItem = async () => {
    if (!report) return;

    try {
      await barrattAPI.addSpendLeakageItem(report.id, {
        po_number: newItem.po_number || undefined,
        description: newItem.description,
        category: newItem.category,
        spend_amount: parseFloat(newItem.spend_amount),
        contracted_price: newItem.contracted_price ? parseFloat(newItem.contracted_price) : undefined,
        transaction_date: newItem.transaction_date,
        notes: newItem.notes || undefined,
      });
      setAddItemDialogOpen(false);
      setNewItem(EMPTY_NEW_ITEM);
      toast.success('Leakage item added successfully');
      refreshAll();
    } catch (err) {
      console.error('Failed to add item:', err);
      toast.error('Failed to add leakage item');
    }
  };

  const handleRecalculate = async () => {
    if (!report) return;

    setRecalculating(true);
    try {
      await barrattAPI.recalculateSpendLeakageReport(report.id);
      toast.success('Report recalculated successfully');
      refreshAll();
    } catch (err) {
      console.error('Failed to recalculate:', err);
      toast.error('Failed to recalculate report');
    } finally {
      setRecalculating(false);
    }
  };

  const handlePublish = async () => {
    if (!report) return;

    setPublishing(true);
    try {
      await barrattAPI.publishSpendLeakageReport(report.id);
      toast.success('Report published successfully');
      setPublishDialogOpen(false);
      loadReport();
    } catch (err) {
      console.error('Failed to publish:', err);
      toast.error('Failed to publish report');
    } finally {
      setPublishing(false);
    }
  };

  const handleArchive = async () => {
    if (!report) return;

    setArchiving(true);
    try {
      await barrattAPI.archiveSpendLeakageReport(report.id);
      toast.success('Report archived successfully');
      setArchiveDialogOpen(false);
      navigate('/spend-leakage');
    } catch (err) {
      console.error('Failed to archive:', err);
      toast.error('Failed to archive report');
    } finally {
      setArchiving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Report Not Found</h3>
              <p className="text-gray-500 mb-4">
                {error || 'The requested spend leakage report could not be found.'}
              </p>
              <Button onClick={() => navigate('/spend-leakage')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Reports
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate('/spend-leakage')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold">{report.title}</h1>
              <Badge className={statusColors[report.status]}>
                {report.status_display}
              </Badge>
            </div>
            <p className="text-gray-500">
              Report #{report.report_number} | {format(new Date(report.period_start), 'dd MMM')} -{' '}
              {format(new Date(report.period_end), 'dd MMM yyyy')}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={refreshAll}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {report.status === 'draft' && (
            <>
              <Button variant="outline" onClick={handleRecalculate} disabled={recalculating}>
                {recalculating ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Calculator className="h-4 w-4 mr-2" />
                )}
                Recalculate
              </Button>
              <Button onClick={() => setPublishDialogOpen(true)}>Publish Report</Button>
            </>
          )}
          {report.status === 'published' && (
            <Button variant="outline" onClick={() => setArchiveDialogOpen(true)}>
              <Archive className="h-4 w-4 mr-2" />
              Archive
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Spend</p>
                <p className="text-2xl font-bold">{formatCurrency(report.total_spend)}</p>
              </div>
              <DollarSign className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Contracted Spend</p>
                <p className="text-2xl font-bold">{formatCurrency(report.contracted_spend)}</p>
              </div>
              <DollarSign className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-red-50 border-red-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600">Leakage Amount</p>
                <p className="text-2xl font-bold text-red-700">{formatCurrency(report.leakage_spend)}</p>
              </div>
              <TrendingDown className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Leakage %</p>
                <p className={`text-2xl font-bold ${toNumber(report.leakage_percentage) > 10 ? 'text-red-600' : ''}`}>
                  {formatPercentage(report.leakage_percentage)}%
                </p>
              </div>
              <Percent className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Uploaded vs not-uploaded split — only on reports that carry it */}
      {hasUploadSplit(report) && (
        <LeakageUploadSplitTiles report={report} formatCurrency={formatCurrency} />
      )}

      {/* Description */}
      {report.description && (
        <Card>
          <CardHeader>
            <CardTitle>Description</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600">{report.description}</p>
          </CardContent>
        </Card>
      )}

      {/* Leakage Items */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Leakage Items</CardTitle>
            <CardDescription>
              {leakageItems.loading ? 'Loading…' : `${leakageItems.count.toLocaleString()} items identified`}
            </CardDescription>
          </div>
          {report.status === 'draft' && (
            <Button onClick={() => setAddItemDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Item
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <LeakageItemsTable {...leakageItems} formatCurrency={formatCurrency} reportNumber={report.report_number} />
        </CardContent>
      </Card>

      {/* Report Metadata */}
      <Card>
        <CardHeader>
          <CardTitle>Report Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Created By</p>
              <p className="font-medium">{report.created_by_name || 'Unknown'}</p>
            </div>
            <div>
              <p className="text-gray-500">Created At</p>
              <p className="font-medium">{format(new Date(report.created_at), 'dd MMM yyyy HH:mm')}</p>
            </div>
            <div>
              <p className="text-gray-500">Last Updated</p>
              <p className="font-medium">{format(new Date(report.updated_at), 'dd MMM yyyy HH:mm')}</p>
            </div>
            {report.published_at && (
              <div>
                <p className="text-gray-500">Published At</p>
                <p className="font-medium">{format(new Date(report.published_at), 'dd MMM yyyy HH:mm')}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <AddLeakageItemDialog
        open={addItemDialogOpen}
        onOpenChange={setAddItemDialogOpen}
        value={newItem}
        onChange={setNewItem}
        onSubmit={() => void handleAddItem()}
      />
      <PublishReportDialog
        open={publishDialogOpen}
        onOpenChange={setPublishDialogOpen}
        report={report}
        busy={publishing}
        onConfirm={() => void handlePublish()}
        formatCurrency={formatCurrency}
      />
      <ArchiveReportDialog
        open={archiveDialogOpen}
        onOpenChange={setArchiveDialogOpen}
        report={report}
        busy={archiving}
        onConfirm={() => void handleArchive()}
        formatCurrency={formatCurrency}
      />
    </div>
  );
};

export default SpendLeakageDetail;
